import { useTheme, makeStyles, Paper, Button, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { RouteComponentProps, useHistory } from 'react-router';
import { useMst } from '../../core/stores';
import { OrderSummaryReadout } from '../../components/OrderSummaryReadout/OrderSummaryReadout';
import { AutomationActions } from '../../components/AutomationActions/AutomationActions';
import { Reducer, useCallback, useEffect, useReducer, useState } from 'react';
import {
  AutomationAction,
  generateActionsList,
  actionOrderTransforms,
} from '../../components/AutomationActions/AutomationActionUtils';
import { OrderStatus } from '../../models';
import { LoadingButton } from '@jht/react-library';
import { ClipLoader } from 'react-spinners';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '60%',
    margin: 'auto',
    '& > *': {
      margin: theme.spacing(2),
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  button: {
    marginLeft: '1rem',
  },
  errorText: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
}));

export interface ManageOrderDetailProps extends RouteComponentProps<{ id: string }> {}

export const ManageOrderDetails = observer(({ match }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const orderId = match.params.id;
  const { digitalOrdersStore, automationStore } = useMst();
  const [currentOrder, setCurrentOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const initialLoad = useCallback(async () => {
    await Promise.all([
      digitalOrdersStore.loadAllFacilities(),
      digitalOrdersStore.loadOrganizations(),
    ]);

    await digitalOrdersStore.loadDigitalOrders({});
    setCurrentOrder(digitalOrdersStore.order(orderId));
    dispatch({ type: 'RESET' });
  }, [digitalOrdersStore]);

  useEffect(() => {
    setLoading(true);
    initialLoad();
    setLoading(false);
  }, [initialLoad]);

  const automationActionReducer = (prevState, action) => {
    if (action.type === 'SET_ACTION_STATUS') {
      const newArr = [...prevState];
      newArr[action.idx].status = action.status;
      return newArr;
    }
    if (action.type === 'SET_SUBTITLE') {
      const newArr = [...prevState];
      newArr[action.idx].subtitle = action.subtitle;
      return newArr;
    }
    if (action.type === 'RESET') {
      const newArr = generateActionsList(currentOrder);
      return newArr;
    }
    return prevState;
  };

  const [automationActions, dispatch] = useReducer<
    Reducer<AutomationAction[], { type: string; idx: number; status?: string; subtitle?: string }>
  >(automationActionReducer, []);
  const [rejecting, setRejecting] = useState(false);
  const [fulfilling, setFulfilling] = useState(false);
  const [automationError, setAutomationError] = useState('');

  const fulfillOrder = async () => {
    setFulfilling(true);
    let currentIdx =
      currentOrder.orderActionsComplete > -1 ? currentOrder.orderActionsComplete + 1 : 0;
    let error;
    let args = [];
    let orgFacMaps = { organizations: {}, facilities: {} };
    const facilities = [];
    let orderActionsComplete;

    while (currentIdx < automationActions.length && !error) {
      try {
        const currentAction = automationActions[currentIdx];
        dispatch({ type: 'SET_ACTION_STATUS', idx: currentIdx, status: 'IN_PROGRESS' });

        const body = actionOrderTransforms[currentAction.action]({
          order: currentOrder,
          maps: orgFacMaps,
          entity: currentAction.entity || undefined,
        });

        if (currentAction.action === 'updateOrgContact') {
          args = [
            orgFacMaps.organizations[currentAction.entity.name] ??
              currentOrder.organization?.organizationId,
            body,
          ];
        } else if (currentAction.action === 'updateFacContact') {
          const facId =
            orgFacMaps.facilities[currentAction.entity.name] ?? currentAction.entity?.facilityId;
          facilities.push(facId);
          args = [facId, body];
        } else if (currentAction.action === 'updateFacEcofit') {
          args = [
            currentAction.entity.facilityId ?? orgFacMaps.facilities[currentAction.entity.name],
            body,
          ];
        } else {
          args = [body];
        }
        const response: any = await automationStore[currentAction.action](...args);
        orderActionsComplete = currentIdx;

        if (currentAction.action === 'createOrg') {
          orgFacMaps.organizations[currentAction.entity.name] = response.id;
          digitalOrdersStore.writeToCurrentOrder(
            orderId,
            { organization: { ...currentOrder.organization, organizationId: response.id } },
            OrderStatus.SUBMITTED,
          );
          dispatch({
            type: 'SET_SUBTITLE',
            idx: currentIdx,
            subtitle: `${currentAction.subtitle} (Created Organization ID: ${response.id})`,
          });
        } else if (currentAction.action === 'createFac') {
          orgFacMaps.facilities[currentAction.entity.name] = response.id;
          const newObj = {
            facility: {
              ...currentOrder.facility,
              facilities: currentOrder.facility.facilities.map(f => ({
                ...f,
                facilityId: f.facilityId ?? orgFacMaps.facilities[f.name],
              })),
            },
          };
          digitalOrdersStore.writeToCurrentOrder(orderId, newObj, OrderStatus.SUBMITTED);
          dispatch({
            type: 'SET_SUBTITLE',
            idx: currentIdx,
            subtitle: `${currentAction.subtitle} (Created Facility ID: ${response.id})`,
          });
        }

        dispatch({ type: 'SET_ACTION_STATUS', idx: currentIdx, status: 'SUCCESS' });

        currentIdx++;
      } catch (e) {
        error = e;
        digitalOrdersStore.writeToCurrentOrder(
          orderId,
          { orderActionsComplete: orderActionsComplete },
          OrderStatus.SUBMITTED,
        );
        await digitalOrdersStore.submitDigitalOrder(orderId);
        dispatch({ type: 'SET_ACTION_STATUS', idx: currentIdx, status: 'FAILURE' });
        setAutomationError(
          'There is an error completing this digital order. Please complete manually.',
        );
        setFulfilling(false);
      }
    }
    if (!error) {
      digitalOrdersStore.setOrderStatus(orderId, OrderStatus.FULFILLED);
      digitalOrdersStore.setOrderFulfiller(orderId);
      facilities.forEach(async facId => {
        // add digital products in Facility Service
        await automationStore.addDigitalProducts(facId, {
          digitalProducts: getDigitalProductsFromOrder(currentOrder.products),
        });
      });
      await digitalOrdersStore.submitDigitalOrder(orderId);
      setFulfilling(false);
      history.push('/manage-orders?status=Submitted');
    }
  };

  const rejectOrder = async () => {
    setRejecting(true);
    digitalOrdersStore.setOrderStatus(orderId, OrderStatus.REJECTED);
    await digitalOrdersStore.submitDigitalOrder(orderId);
    setRejecting(false);
    history.push('/manage-orders?status=Submitted');
  };

  const getDigitalProductsFromOrder = products => {
    const allProducts = { ...products.productSelection, ...products.integrations };
    const productsArr = Object.keys(allProducts).filter(product => product !== 'other');
    productsArr.forEach((product, i) => {
      switch (product) {
        case 'AM':
          productsArr[i] = 'ASSET_MANAGEMENT';
          return;
        case 'AM_VISION':
          productsArr[i] = 'VISION_ASSET_MANAGEMENT';
          return;
        case 'COM360':
          productsArr[i] = 'COMMUNITY_360';
          return;
        case 'ENG360':
          productsArr[i] = 'ENGAGE_360';
          return;
        case 'FACILITY360':
          productsArr[i] = 'FACILITY_360';
          return;
        case 'iFIT':
          productsArr[i] = 'IFIT';
          return;
        case 'PACE':
          productsArr[i] = 'SECA';
          return;
        case 'EGYM':
          productsArr[i] = 'EGYM';
          return;
        case 'ECOFIT':
          productsArr[i] = 'ECOFIT';
          return;
        default:
          return;
      }
    });
    return productsArr;
  };

  return (
    <Paper className={classes.container}>
      {loading && (
        <ClipLoader size={40} className="!border-purple-500 !border-4 !border-b-transparent mr-2" />
      )}
      {currentOrder && (
        <>
          <OrderSummaryReadout order={currentOrder} orderId={orderId} showLinks={false} />
          {(currentOrder.orderStatus === OrderStatus.SUBMITTED ||
            currentOrder.orderStatus === OrderStatus.FULFILLED) && (
            <>
              <AutomationActions actionList={automationActions} />
              <div className={classes.errorText}>
                {automationError && (
                  <Typography color="error" variant="subtitle2" className={classes.errorText}>
                    {automationError}
                  </Typography>
                )}
              </div>
            </>
          )}
          {currentOrder.orderStatus === 'Submitted' && (
            <div className={classes.buttons}>
              <LoadingButton
                variant="contained"
                className={classes.button}
                color="secondary"
                disabled={
                  rejecting ||
                  fulfilling ||
                  automationError.length > 0 ||
                  currentOrder.orderStatus === OrderStatus.REJECTED ||
                  currentOrder.orderStatus === OrderStatus.FULFILLED
                }
                onClick={() => rejectOrder()}
                loading={rejecting}
              >
                Reject
              </LoadingButton>
              <LoadingButton
                variant="contained"
                className={classes.button}
                color="primary"
                disabled={
                  rejecting ||
                  fulfilling ||
                  automationError.length > 0 ||
                  currentOrder.orderStatus === OrderStatus.REJECTED ||
                  currentOrder.orderStatus === OrderStatus.FULFILLED
                }
                onClick={() => fulfillOrder()}
                loading={fulfilling}
              >
                Fulfill
              </LoadingButton>
            </div>
          )}
        </>
      )}
    </Paper>
  );
});
